import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import StateSelector from '../components/common/StateSelector';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/common/NotificationContainer';
import ModernBackground from '../components/common/ModernBackground';

import { Field } from '../types/field';
import { getTopFields, getFieldsByState, searchFields } from '../services/fieldService';

const withAlpha = (hexColor: string, alpha: number) => {
  const hex = hexColor.replace('#', '');
  const normalized = hex.length === 3
    ? hex.split('').map(char => `${char}${char}`).join('')
    : hex;
  const bigint = parseInt(normalized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const isShadowTheme = (theme: any) => theme.name === 'shadowGrid';
const isModernTheme = (theme: any) => theme.name === 'modernWarfare';
const isNightOps = (theme: any) => theme.name === 'nightOps';

const cardBorder = (theme: any) => theme.profile?.cards?.border || theme.colors.borderLight;

const PageBackground = styled.div<{ $shadow?: boolean }>`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme.profile.pageBackground === 'transparent'
      ? 'transparent'
      : theme.gradients.background || theme.colors.background};
  padding: 20px;
  padding-top: 100px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding-top: 90px;
    padding: 10px;
    padding-top: 90px;
  }
`;

const DottedOverlay = styled.div`
  display: none;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  color: ${({ theme }) => theme.colors.text};
  position: relative;
  z-index: 3;
  font-family: ${({ theme }) => {
    if (theme.name === 'shadowGrid') return "'Courier New', monospace";
    if (theme.name === 'modernWarfare') return "'Chakra Petch', sans-serif";
    return 'inherit';
  }};
  
  @media (max-width: 1240px) {
    max-width: 95%;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

// Game Type Selector Components
const GameTypeSelector = styled.div`
  display: flex;
  justify-content: center;
  gap: 2px;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    margin-bottom: 25px;
  }
`;

const GameTypeButton = styled.button<{ $active: boolean }>`
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : "'Courier New', monospace"};
  font-size: 14px;
  padding: 12px 24px;
  background: ${props => props.$active ? props.theme.colors.primary : withAlpha(props.theme.colors.primary, 0.1)};
  color: ${props => props.$active ? (isShadowTheme(props.theme) ? '#000000' : '#00121f') : props.theme.colors.primary};
  border: 2px solid ${props => props.theme.colors.primary};
  cursor: pointer;
  text-transform: uppercase;
  font-weight: bold;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.$active ? props.theme.colors.primary : withAlpha(props.theme.colors.primary, 0.2)};
    box-shadow: 0 0 15px ${props => withAlpha(props.theme.colors.primary, 0.4)};
  }

  &:first-child {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
    border-right: 1px solid ${props => props.theme.colors.primary};
  }

  &:last-child {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
    border-left: 1px solid ${props => props.theme.colors.primary};
  }

  @media (max-width: 768px) {
    padding: 15px 20px;
    font-size: 16px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0 0 10px 0;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 0 0 15px ${({ theme }) => withAlpha(theme.colors.primary, 0.7)};
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "inherit"};

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 15px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.secondary};
  margin: 0;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.4;
    padding: 0 10px;
  }
`;

const FilterSection = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 30px;
  backdrop-filter: none;
`;

const FilterTitle = styled.h3`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.4rem;
  font-weight: bold;
  margin: 0 0 20px 0;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "'Courier New', monospace"};
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  align-items: end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterLabel = styled.label`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.9rem;
  font-weight: bold;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 25px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const FieldCard = styled(Link)`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 12px;
  padding: 25px;
  text-decoration: none;
  color: inherit;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 15px;
  backdrop-filter: none;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-5px);
    box-shadow: 0 8px 25px ${({ theme }) => withAlpha(theme.colors.primary, 0.3)};
  }
`;

const FieldHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 15px;
`;

const FieldName = styled.h3`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.3rem;
  font-weight: bold;
  margin: 0;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "'Courier New', monospace"};
  line-height: 1.2;
`;

const FieldRank = styled.div`
  background: linear-gradient(135deg, #4caf50, #45a049);
  color: white;
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: bold;
  text-transform: uppercase;
  white-space: nowrap;
`;

const FieldInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
`;

const InfoLabel = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
`;

const InfoValue = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-weight: bold;
`;

const GameTypes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const GameTypeTag = styled.span`
  background: ${({ theme }) => withAlpha(theme.colors.primary, 0.2)};
  color: ${({ theme }) => theme.colors.primary};
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
  text-transform: uppercase;
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.primary, 0.3)};
`;

const StatusBadge = styled.span<{ $status: 'approved' | 'pending' }>`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
  text-transform: uppercase;
  
  ${props => props.$status === 'approved' ? `
    background: rgba(76, 175, 80, 0.2);
    color: #4caf50;
    border: 1px solid #4caf50;
  ` : `
    background: rgba(255, 193, 7, 0.2);
    color: #ffc107;
    border: 1px solid #ffc107;
  `}
`;

const FieldDescription = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const SectionBlock = styled.section`
  margin-bottom: 50px;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 25px;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  text-shadow: 0 0 12px ${({ theme }) => withAlpha(theme.colors.primary, 0.4)};
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "inherit"};

  @media (max-width: 768px) {
    font-size: 1.7rem;
    margin-bottom: 20px;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
  
  &::after {
    content: '';
    width: 50px;
    height: 50px;
    border: 4px solid ${({ theme }) => withAlpha(theme.colors.primary, 0.3)};
    border-radius: 50%;
    border-top-color: ${({ theme }) => theme.colors.primary};
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${({ theme }) => theme.colors.textMuted};
  
  h3 {
    color: ${({ theme }) => theme.colors.primary};
    font-size: 1.5rem;
    margin-bottom: 15px;
  }
  
  p {
    font-size: 1.1rem;
    line-height: 1.6;
  }
`;

// Search components - updated to match Commandos style
const SearchSection = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  align-items: center;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 12px;
  padding: 20px;
  backdrop-filter: none;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
    margin-bottom: 25px;
    padding: 15px;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  background: ${({ theme }) => withAlpha(theme.colors.primary, 0.1)};
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.primary, 0.3)};
  border-radius: 8px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  font-family: inherit;
  transition: all 0.3s ease;

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 8px ${({ theme }) => withAlpha(theme.colors.primary, 0.5)};
    background: ${({ theme }) => withAlpha(theme.colors.primary, 0.15)};
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 15px 20px;
    font-size: 16px;
    border-radius: 10px;
  }
`;

const ActionButton = styled.button`
  background: ${({ theme }) => withAlpha(theme.colors.primary, 0.1)};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  padding: 12px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : "'Orbitron', sans-serif"};

  &:hover {
    background: ${({ theme }) => withAlpha(theme.colors.primary, 0.2)};
    box-shadow: 0 0 15px ${({ theme }) => withAlpha(theme.colors.primary, 0.4)};
  }

  @media (max-width: 768px) {
    width: 100%;
    padding: 15px 20px;
    font-size: 15px;
  }
`;

const SearchingIndicator = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.colors.primary};
  margin: 20px 0;
  font-style: italic;
`;

const SearchResultsSection = styled.div`
  margin-bottom: 40px;
`;

const SearchResultsTitle = styled.h2`
  font-size: 1.8rem;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 20px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 25px;
  }
`;

const FieldsPage: React.FC = () => {
  const navigate = useNavigate();
  const { notifications, showNotification, removeNotification } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [topFields, setTopFields] = useState<Field[]>([]);
  const [stateFields, setStateFields] = useState<Field[]>([]);
  const [selectedState, setSelectedState] = useState('All states');
  const [stateLoading, setStateLoading] = useState(false);
  
  const isShadowGrid = theme.name === 'shadowGrid';
  const isModernWarfare = theme.name === 'modernWarfare';
  const isNightOpsMode = theme.name === 'nightOps';
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Field[]>([]);
  const [searching, setSearching] = useState(false);
  
  // Game type state
  const [selectedGameType, setSelectedGameType] = useState(searchParams.get('type') || 'all');
  
  // Sync selectedGameType with URL params
  useEffect(() => {
    const typeFromUrl = searchParams.get('type') || 'all';
    setSelectedGameType(typeFromUrl);
  }, [searchParams]);
  
  // Ref для таймера поиска
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);


  // Функция для переключения типа игры
  const handleGameTypeChange = (gameType: string) => {
    console.log('Changing game type to:', gameType);
    setSelectedGameType(gameType);
    if (gameType === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ type: gameType });
    }
    // Сброс состояния при смене типа
    setSearchTerm('');
    setSearchResults([]);
    // Данные перезагрузятся автоматически через useEffect
  };

  const handleRegisterFieldClick = () => {
    navigate('/register-field');
  };

  // Определяем заголовок страницы на основе выбранного типа
  const getPageTitle = () => {
    switch (selectedGameType) {
      case 'airsoft':
        return 'NovaWarfare Airsoft Fields';
      case 'paintball':
        return 'NovaWarfare Paintball Fields';
      case 'speedball':
        return 'NovaWarfare Speedball Fields';
      default:
        return 'NovaWarfare Combat Fields';
    }
  };

  const getPageSubtitle = () => {
    switch (selectedGameType) {
      case 'airsoft':
        return 'Elite tactical airsoft facilities across the United States';
      case 'paintball':
        return 'Premier paintball arenas and outdoor fields nationwide';
      case 'speedball':
        return 'High-speed tournament speedball facilities';
      default:
        return 'Discover the best tactical combat facilities across the United States';
    }
  };

  useEffect(() => {
    loadTopFields();
  }, [selectedGameType]);

  useEffect(() => {
    if (selectedState) {
      loadStateFields(selectedState);
    }
  }, [selectedState, selectedGameType]);

  const loadTopFields = async () => {
    try {
      setLoading(true);
      const gameType = selectedGameType === 'all' ? undefined : selectedGameType;
      console.log('Loading top fields with gameType:', gameType);
      const fields = await getTopFields(10, gameType);
      console.log('Loaded fields:', fields.length);
      setTopFields(fields);
      setLoading(false);
    } catch (error: any) {
      console.error('Error loading top fields:', error);
      showNotification('error', 'Error', 'Failed to load top fields');
      setLoading(false);
    }
  };

  const loadStateFields = async (state: string) => {
    try {
      setStateLoading(true);
      const gameType = selectedGameType === 'all' ? undefined : selectedGameType;
      console.log('Loading state fields with gameType:', gameType, 'state:', state);
      const fields = await getFieldsByState(state, 50, gameType);
      console.log('Loaded state fields:', fields.length);
      setStateFields(fields);
      setStateLoading(false);
    } catch (error: any) {
      console.error('Error loading state fields:', error);
      showNotification('error', 'Error', `Failed to load fields for ${state}`);
      setStateLoading(false);
    }
  };

  const formatGameType = (gameType: string): string => {
    return gameType.charAt(0).toUpperCase() + gameType.slice(1);
  };

  // Search functions
  const handleSearch = async (term: string) => {
    if (!term.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const gameType = selectedGameType === 'all' ? undefined : selectedGameType;
      console.log('Searching fields with gameType:', gameType, 'term:', term);
      const results = await searchFields(term, 20, gameType);
      console.log('Search results:', results?.length);
      setSearchResults(results || []);
    } catch (err: any) {
      console.error('Error searching fields:', err);
      showNotification('error', 'Error', 'Failed to search fields');
    } finally {
      setSearching(false);
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Очищаем предыдущий таймер
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Дебаунс поиска
    if (value.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        handleSearch(value);
      }, 500);
    } else {
      setSearchResults([]);
    }
  };

  const renderFieldCard = (field: Field, index: number) => (
    <FieldCard key={field.id} to={`/fields/${field.id}`}>
      <FieldHeader>
        <FieldName>{field.name}</FieldName>
        <FieldRank>#{index + 1}</FieldRank>
      </FieldHeader>
      
      <FieldInfo>
        <InfoRow>
          <InfoLabel>State:</InfoLabel>
          <InfoValue>{field.state || 'Unknown'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Rating:</InfoLabel>
          <InfoValue>{field.rating ? `${field.rating}/5.0` : 'Not rated'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Status:</InfoLabel>
          <StatusBadge $status={field.isApproved ? 'approved' : 'pending'}>
            {field.isApproved ? 'Active' : 'Pending'}
          </StatusBadge>
        </InfoRow>
      </FieldInfo>
      
      <GameTypes>
        {field.supportedGameTypes.map(gameType => (
          <GameTypeTag key={gameType}>
            {formatGameType(gameType)}
          </GameTypeTag>
        ))}
      </GameTypes>
      
      {field.description && (
        <FieldDescription>{field.description}</FieldDescription>
      )}
    </FieldCard>
  );

  return (
    <PageBackground $shadow={isShadowGrid}>
      {isNightOpsMode && <DottedOverlay />}
      {isModernWarfare && <ModernBackground />}
      <Container>
        <Header>
          <Title>{getPageTitle()}</Title>
          <Subtitle>{getPageSubtitle()}</Subtitle>
        </Header>

          {/* Game Type Filter */}
          <GameTypeSelector>
            <GameTypeButton 
              $active={selectedGameType === 'all'}
              onClick={() => handleGameTypeChange('all')}
            >
              [ALL GAMES]
            </GameTypeButton>
            <GameTypeButton 
              $active={selectedGameType === 'airsoft'}
              onClick={() => handleGameTypeChange('airsoft')}
            >
              [AIRSOFT]
            </GameTypeButton>
            <GameTypeButton 
              $active={selectedGameType === 'paintball'}
              onClick={() => handleGameTypeChange('paintball')}
            >
              [PAINTBALL]
            </GameTypeButton>
            <GameTypeButton 
              $active={selectedGameType === 'speedball'}
              onClick={() => handleGameTypeChange('speedball')}
            >
              [SPEEDBALL]
            </GameTypeButton>
          </GameTypeSelector>

          {/* Search Section */}
          <SearchSection>
            <SearchInput
              type="text"
              placeholder="Search fields by name or location..."
              value={searchTerm}
              onChange={handleSearchInputChange}
            />
            <ActionButton onClick={handleRegisterFieldClick}>
              REGISTER FIELD
            </ActionButton>
          </SearchSection>

          {searching && (
            <SearchingIndicator>Searching fields...</SearchingIndicator>
          )}

          {searchResults.length > 0 && (
            <SearchResultsSection>
              <SearchResultsTitle>Search Results</SearchResultsTitle>
              <FieldsGrid>
                {searchResults.map((field, index) => renderFieldCard(field, index))}
              </FieldsGrid>
            </SearchResultsSection>
          )}

          {/* Top 10 Fields Section */}
          <SectionBlock>
            <SectionTitle>Top 10 Fields Nationwide</SectionTitle>
            {loading ? (
              <LoadingSpinner />
            ) : topFields.length > 0 ? (
              <FieldsGrid>
                {topFields.map((field, index) => renderFieldCard(field, index))}
              </FieldsGrid>
            ) : (
              <EmptyState>
                <h3>No Top Fields Available</h3>
                <p>Check back later for the latest field rankings.</p>
              </EmptyState>
            )}
          </SectionBlock>

          {/* State Filter Section */}
          <FilterSection>
            <FilterTitle>Filter by State</FilterTitle>
            <FilterGrid>
              <FilterGroup>
                <FilterLabel>Select State</FilterLabel>
                <StateSelector
                  value={selectedState}
                  onChange={setSelectedState}
                  placeholder="Choose a state..."
                />
              </FilterGroup>
            </FilterGrid>
          </FilterSection>

          {/* State Fields Section */}
          <SectionBlock>
            <SectionTitle>Fields in {selectedState}</SectionTitle>
            {stateLoading ? (
              <LoadingSpinner />
            ) : stateFields.length > 0 ? (
              <FieldsGrid>
                {stateFields.map((field, index) => renderFieldCard(field, index))}
              </FieldsGrid>
            ) : (
              <EmptyState>
                <h3>No Fields Found in {selectedState}</h3>
                <p>Try selecting a different state or check back later for new field registrations.</p>
              </EmptyState>
            )}
          </SectionBlock>

        <NotificationContainer 
          notifications={notifications} 
          onRemove={removeNotification} 
        />
      </Container>
    </PageBackground>
  );
};

export default FieldsPage;
